import type { Anlage } from "../api/typen";
import { FELD_GRUPPEN, type FeldDefinition, type FeldName, type FormWerte } from "../lib/felder";
import { AMPEL_KLASSEN, GEPRUEFT, ampel } from "../lib/konfidenz";
import { KonfidenzAnzeige } from "./Konfidenz";
import { ZerezSucheKnopf } from "./ZerezSuche";

interface Props {
  anlage: Anlage;
  werte: FormWerte;
  fehler: Partial<Record<FeldName, string>>;
  geaendert: Set<FeldName>;
  /** Unveränderte Werte, die der Sachbearbeiter als korrekt markiert hat. */
  markiert: Set<FeldName>;
  nurLesen: boolean;
  beiAenderung: (name: FeldName, wert: string) => void;
  beiMarkieren: (name: FeldName) => void;
}

function eingabeKlassen(stufe: ReturnType<typeof ampel>, fehler: boolean, geaendert: boolean): string {
  if (fehler) return "border-ampel-rot bg-ampel-rotHell/40 focus:ring-ampel-rot";
  if (geaendert) return "border-sky-500 bg-sky-50/50 focus:ring-sky-500";
  if (stufe === "rot") return "border-2 border-ampel-rot focus:ring-ampel-rot";
  if (stufe === "gelb") return "border-2 border-ampel-gelb focus:ring-ampel-gelb";
  return "border-neutral-300 focus:ring-neutral-900";
}

function Feld({ def, props }: { def: FeldDefinition; props: Props }) {
  const { anlage, werte, fehler, geaendert, markiert, nurLesen, beiAenderung, beiMarkieren } = props;
  const wert = werte[def.name];
  const istGeaendert = geaendert.has(def.name);
  const istMarkiert = !istGeaendert && markiert.has(def.name);
  const konfidenz = istGeaendert || istMarkiert ? GEPRUEFT : anlage.konfidenz[def.name];
  const stufe = ampel(konfidenz, wert.trim() !== "");
  const meldung = fehler[def.name] ?? (istGeaendert ? undefined : anlage.validierung[def.name]);
  const markierbar = !nurLesen && !istGeaendert && wert.trim() !== "" && (stufe === "gelb" || stufe === "rot" || istMarkiert);
  const id = `feld-${def.name}`;
  const klassen = `block w-full rounded-lg border bg-white px-3 py-2 text-sm shadow-sm outline-none transition focus:ring-2 focus:ring-offset-0 disabled:bg-neutral-50 disabled:text-neutral-700 ${eingabeKlassen(stufe, Boolean(meldung), istGeaendert || istMarkiert)}`;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-xs font-semibold text-neutral-700">
          {def.label}
          {def.pflicht && <span className="text-ampel-rot" title="Pflichtfeld für die Bestätigung"> *</span>}
          {def.einheit && <span className="font-normal text-neutral-400"> ({def.einheit})</span>}
        </label>
        <span className="flex items-center gap-2">
          {markierbar && (
            <button
              type="button"
              onClick={() => beiMarkieren(def.name)}
              className={`rounded-md border px-1.5 py-0.5 text-[11px] font-semibold transition ${
                istMarkiert ? "border-sky-500 bg-sky-50 text-sky-700" : "border-neutral-300 text-neutral-600 hover:border-neutral-500"
              }`}
              title={istMarkiert ? "Markierung aufheben" : "Wert stimmt mit dem Original überein – als geprüft markieren"}
            >
              {istMarkiert ? "✓ bestätigt" : "✓ Stimmt"}
            </button>
          )}
          {istGeaendert ? (
            <span className="text-xs font-semibold text-sky-700">✎ geändert</span>
          ) : istMarkiert ? null : (
            <KonfidenzAnzeige konfidenz={konfidenz} hatWert={wert.trim() !== ""} />
          )}
        </span>
      </div>
      {def.art === "janein" ? (
        <select id={id} className={klassen} value={wert} disabled={nurLesen} onChange={(e) => beiAenderung(def.name, e.target.value)}>
          <option value="">unbekannt</option>
          <option value="ja">Ja – unterschrieben</option>
          <option value="nein">Nein – fehlt</option>
        </select>
      ) : (
        <input
          id={id}
          type={def.art === "datum" ? "date" : "text"}
          inputMode={def.art === "zahl" ? "decimal" : undefined}
          autoComplete="off"
          spellCheck={false}
          className={klassen}
          value={wert}
          placeholder={def.platzhalter}
          disabled={nurLesen}
          aria-invalid={Boolean(meldung)}
          aria-describedby={meldung ? `${id}-fehler` : undefined}
          onChange={(e) => beiAenderung(def.name, e.target.value)}
        />
      )}
      {def.name === "zerez_id" && !nurLesen && (
        <ZerezSucheKnopf anlageId={anlage.id} wechselrichter={werte.wechselrichter} beiWahl={(zerezId) => beiAenderung("zerez_id", zerezId)} />
      )}
      {meldung && (
        <p id={`${id}-fehler`} className="mt-1 text-xs text-ampel-rot">
          {meldung}
        </p>
      )}
      {!meldung && !istGeaendert && !istMarkiert && stufe !== "gruen" && stufe !== "keine" && konfidenz !== undefined && (
        <p className={`mt-1 text-xs ${AMPEL_KLASSEN[stufe].text}`}>
          {stufe === "rot" ? "Unsicher erkannt – bitte mit dem Original abgleichen" : "Bitte kurz prüfen"}
        </p>
      )}
    </div>
  );
}

export function AnlagenFormular(props: Props) {
  return (
    <div className="space-y-6">
      {FELD_GRUPPEN.map((gruppe) => (
        <fieldset key={gruppe.titel} className="space-y-3">
          <legend className="mb-2 text-sm font-semibold uppercase tracking-wide text-neutral-500">{gruppe.titel}</legend>
          {gruppe.titel === "Vollmacht" && (
            <p className="text-xs text-neutral-600">
              Vollmacht-Dokument: {props.anlage.vollmacht_vorhanden ? "vorhanden ✓" : <span className="font-semibold text-ampel-rot">fehlt</span>}
            </p>
          )}
          <div className={`grid gap-4 ${gruppe.felder.length > 2 ? "sm:grid-cols-2" : ""}`}>
            {gruppe.felder.map((def) => (
              <div key={def.name} className={def.name === "strasse" || def.name === "modultyp" || def.name === "wechselrichter" || def.name === "iban" ? "sm:col-span-2" : ""}>
                <Feld def={def} props={props} />
              </div>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
